import { rankedPage, isFeedCursorExpired } from "@/src/services/api/posts/feed-functions";
import { useAuth } from "@/src/contexts/auth.context";
import { useQueryClient } from "@tanstack/react-query";
import { LifeEventType, Post } from "@/src/models/post.model";
import gqlClient from "@/src/services/api/graphql.client";
import {
  fetchFeedPage,
  feedQueryKey,
  mergeBackgroundCards,
  useCreatePostMutation,
  useDeletePostMutation,
  FEED_HEAD_INTERVAL_MS,
  useFeedHeadQuery,
  useGetPostsQuery,
  useUpdatePostMutation,
} from "@/src/services/api/posts/posts.hooks";
import {
  forgetPost,
  reconcileFeed,
  StableFeedState,
} from "@/src/utils/stable-feed";
import { sendNotification } from "@/src/services/api/notification/notification.service";
import { FeedSort } from "@/src/utils/feed-sort";
import { GET_POSTS_QUERY } from "@/src/services/api/posts/posts.query";
import { hot } from "@/src/utils/feed-score";
import {
  groupScopeWhere,
  postBelongsToGroup,
} from "@/src/utils/group-post-scope";
import {
  extractHashtag,
  SEARCH_PAGE_SIZE,
} from "@/src/utils/feed-search.utils";
import { useLocalSearchParams, useRouter } from "expo-router";
import { AppState } from "react-native";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { VisibilityType } from "./use-post-creation-modal";
import { SelectableItem } from "@/src/components/action-modals/item-selection/item-row";

/**
 * What the main feed displayed, per user, org and sort. Module level so it
 * outlives the screen: coming back to the feed shows the same posts in the
 * same order the reader left (see `reconcileFeed`).
 */
const stableFeeds = new Map<string, StableFeedState>();
export const clearStableFeeds = () => stableFeeds.clear();

/** Scrolled less than this from the top counts as "at the top" of the feed. */
export const FEED_TOP_THRESHOLD = 200;

type UsePostsOptions = Readonly<{
  /**
   * Hold the displayed order steady and collect new posts behind an
   * "N new posts" pill instead of inserting them (the main feed).
   */
  stableOrder?: boolean;
}>;

export const usePosts = (
  insideGroup: Boolean,
  groupId?: string,
  options?: UsePostsOptions,
) => {
  const { user, userGroups, isSuperAdmin, orgId } = useAuth();
  const { t } = useTranslation();
  const router = useRouter();

  const { search: searchQueryFromUrl, post_id } = useLocalSearchParams<{
    search?: string;
    post_id?: string;
  }>();

  // Safely resolve string from url param — useLocalSearchParams can return string[]
  const initialSearch = Array.isArray(searchQueryFromUrl)
    ? (searchQueryFromUrl[0] ?? "")
    : (searchQueryFromUrl ?? "");

  const [modalOpen, setModalOpen] = useState(false);
  const [editPost, setEditPost] = useState<Post | null>(null);

  const [search, setSearch] = useState(initialSearch);
  const [debouncedSearch, setDebouncedSearch] = useState(initialSearch);

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(search);
      if (search.trim()) {
        router.setParams({ search: search.trim(), post_id });
      } else {
        router.setParams({ search: undefined, post_id });
      }
    }, 400);
    return () => clearTimeout(timer);
  }, [search]);

  const isSearching = debouncedSearch.trim().length > 0;

  // A deep link into a single post (notification tap, shared /feeds?post_id=…).
  // This used to be held in a module-level latch opened by a mount-only effect,
  // which broke the notification path outright: /feeds is a tab screen that
  // stays mounted, so arriving with a new post_id changed the params without
  // ever remounting the hook, and nothing opened. The latch also outlived the
  // screen, so a stale id could reopen on an unrelated visit.
  //
  // Deriving it from the param and reacting to *that* fixes both: every arrival
  // with a post id opens the viewer, whether the screen was already mounted or
  // not.
  const requestedPostId = Array.isArray(post_id)
    ? (post_id[0] ?? null)
    : (post_id ?? null);

  const [viewerPostId, setViewerPostId] = useState<string | null>(
    requestedPostId,
  );
  const [viewerOpen, setViewerOpen] = useState(!!requestedPostId);

  useEffect(() => {
    if (!requestedPostId) return;
    setViewerPostId(requestedPostId);
    setViewerOpen(true);
  }, [requestedPostId]);

  const [sort, setSort] = useState<FeedSort>("hottest");

  const [deletePostId, setDeletePostId] = useState<string | null>(null);
  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);
  const deletedRankIds = useRef(new Set<string>());

  const [searchResults, setSearchResults] = useState<Post[]>([]);
  const [searchPage, setSearchPage] = useState(1);
  const [hasMoreSearchResults, setHasMoreSearchResults] = useState(false);
  const [isSearchLoading, setIsSearchLoading] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);
  const isSearchingRef = useRef(false);
  const searchRequest = useRef<{ scope: string; page: number } | null>(null);
  const searchScope = JSON.stringify([user?.itemId, orgId, groupId, search, userGroups, isSuperAdmin]);
  const searchScopeRef = useRef(searchScope);
  searchScopeRef.current = searchScope;
  const isLoadingMoreRef = useRef(false);
  const [isBufferDemand, setBufferDemand] = useState(false);

  // Reactor modal
  const [showReactorModal, setReactorModalVisibility] = useState(false);

  // Visibility filter
  const filterPostVisibility = (post: Post) => {
    if (deletedRankIds.current.has(post.ItemId)) return false;
    // A group feed shows only the posts addressed to that group, whatever their
    // visibility. This gate used to live inside `case "group"` alone, so a
    // public post from the main feed fell straight through `case "public"` and
    // surfaced in every group's feed. The paged query is scoped server-side, but
    // search is a separate, unscoped query whose results run through here too —
    // which is where the leak actually showed.
    if (insideGroup && groupId && !postBelongsToGroup(post, groupId)) {
      return false;
    }

    // A super-admin moderates the whole feed, so they see every post regardless
    // of who it was addressed to. Without this they were blind to exactly the
    // content most likely to need moderating — group posts for groups they had
    // not joined, and posts shared with specific people. The group-feed scoping
    // above still applies to them: a group's feed shows that group's posts only.
    if (isSuperAdmin) return true;

    switch (post.visibility) {
      case "public":
        return true;
      case "private":
        return post.CreatedBy === user?.itemId;
      case "specific":
        return post.visibleToUserIds?.includes(user?.itemId ?? "");
      case "group":
        // Inside a group feed the post is already known to belong to this group
        // (checked above) and the screen itself is access-gated, so membership
        // must not be re-derived here: a group's own posts, including the
        // creator's, were being filtered out of its feed.
        if (insideGroup && groupId) return true;
        // In the main feed, honour membership — but a group post carries its
        // group in `groupId`, which the old check ignored entirely.
        if (post.groupId) return userGroups?.includes(post.groupId) ?? false;
        return post.visibleToGroupIds?.some((id: string) =>
          userGroups?.includes(id),
        );
      default:
        return false;
    }
  };

  const {
    data,
    fetchNextPage,
    hasNextPage,
    isFetching,
    isFetchingNextPage,
    isLoading,
    isError,
    error: feedError,
    refetch,
  } = useGetPostsQuery(insideGroup ? groupId : undefined, sort);
  const queryClient = useQueryClient();
  // The ranking function serves pages from a snapshot frozen when page 1 was
  // fetched; its cursors die after a few minutes (or at a boost boundary), and
  // so do any restored from the persisted cache. Refetching from page 1 gets a
  // new snapshot and fresh cursors while the data on screen stays put (the
  // stable order below keeps it from reshuffling) — resetting the query here,
  // as before, blanked the whole feed back to skeletons mid-scroll.
  useEffect(() => {
    if (isFeedCursorExpired(feedError)) void refetch();
  }, [feedError, refetch]);
  // A single parsed page lives outside the persisted query cache until demand
  // consumes it. Its epoch includes audience and query scope.
  const bufferScope = JSON.stringify([user?.itemId, orgId, groupId, sort,
    [...(userGroups ?? [])].sort(), isSuperAdmin, debouncedSearch]);
  const scopeRef = useRef(bufferScope);
  scopeRef.current = bufferScope;
  const [foreground, setForeground] = useState(() => (AppState.currentState == null || AppState.currentState === "active") &&
    (typeof document === "undefined" || document.visibilityState !== "hidden"));
  useEffect(() => {
    const subscription = AppState.addEventListener("change", state => setForeground(state === "active"));
    const visibility = () => setForeground(document.visibilityState !== "hidden");
    if (typeof document !== "undefined") document.addEventListener("visibilitychange", visibility);
    return () => {
      subscription.remove();
      if (typeof document !== "undefined") document.removeEventListener("visibilitychange", visibility);
    };
  }, []);
  type BufferedPage = Awaited<ReturnType<typeof fetchFeedPage>>;
  const buffer = useRef<{ scope: string; param: string | number; promise: Promise<BufferedPage> } | null>(null);
  const nextParam = (() => {
    const last = data?.pages[data.pages.length - 1]?.getposts;
    return last?.nextCursor ?? (last?.hasNextPage && last?.pageNo ? last.pageNo + 1 : undefined);
  })();
  const prefetchMore = useCallback(() => {
    if (!foreground || (typeof navigator !== "undefined" && navigator.onLine === false) || isSearching || !hasNextPage || isFetching || isFetchingNextPage ||
        feedError || nextParam == null || buffer.current) return;
    const entry = { scope: bufferScope, param: nextParam,
      promise: fetchFeedPage(insideGroup ? groupId : undefined, sort, nextParam, user?.itemId, orgId) };
    buffer.current = entry;
    void entry.promise.catch(() => {
      // Remember the failed page until explicit demand; don't retry forever.
    });
  }, [foreground, isSearching, hasNextPage, isFetching, isFetchingNextPage,
      feedError, nextParam, bufferScope, insideGroup, groupId, sort, user?.itemId, orgId]);
  useEffect(() => {
    buffer.current = null;
    isLoadingMoreRef.current = false;
    setBufferDemand(false);
  }, [bufferScope]);
  useEffect(() => {
    if (isFetching && !isFetchingNextPage) buffer.current = null;
    if (buffer.current && buffer.current.param !== nextParam) buffer.current = null;
    prefetchMore();
  }, [nextParam, prefetchMore, isFetching, isFetchingNextPage]);
  useEffect(() => () => { scopeRef.current = "disposed"; buffer.current = null; }, []);
  const posts = useMemo(() => {
    const seen = new Set<string>();
    return (data?.pages.flatMap((page) => page?.getposts?.items ?? []) ?? [])
      .filter(post => { if (seen.has(post.ItemId)) return false; seen.add(post.ItemId); return true; });
  }, [data]);
  const visiblePosts = posts.filter(filterPostVisibility);

  // ---- Stable order + "N new posts" ---------------------------------------
  const stable = !insideGroup && !!options?.stableOrder;
  const feedKey = JSON.stringify([user?.itemId, orgId, sort, [...(userGroups ?? [])].sort(), isSuperAdmin]);
  const atTopRef = useRef(true);
  const acceptingRef = useRef(false);
  const knownPosts = useRef(new Map<string, Post>());
  const knownScope = useRef(feedKey);
  if (knownScope.current !== feedKey) {
    knownScope.current = feedKey;
    knownPosts.current = new Map();
  }
  const [stableView, setStableView] = useState<{
    key: string;
    order: string[];
    pending: string[];
  } | null>(null);

  // Let direct cards paint before starting the optional ranked head. The
  // minute-long query freshness coalesces remounts and prevents queue floods.
  const [headReady, setHeadReady] = useState(false);
  const hasListData = !!data;
  useEffect(() => setHeadReady(false), [feedKey]);
  useEffect(() => {
    if (insideGroup || !hasListData || headReady) return;
    const timer = setTimeout(() => setHeadReady(true), 1000);
    return () => clearTimeout(timer);
  }, [insideGroup, hasListData, headReady]);
  const head = useFeedHeadQuery(
    sort,
    undefined,
    !insideGroup && !hasSearched && headReady,
  );
  const [rankDemand, setRankDemand] = useState(1);
  const [rankTail, setRankTail] = useState<{scope: string; posts: Post[]; cursor: string | null; pages: number} | null>(null);
  const rankFlight = useRef<symbol | null>(null);
  const [rankLoading, setRankLoading] = useState(false);
  useEffect(() => {
    rankFlight.current = null;
    setRankLoading(false);
    setRankDemand(1);
    setRankTail(null);
    deletedRankIds.current.clear();
  }, [bufferScope]);
  useEffect(() => {
    if (!head.data) return;
    setRankDemand(1);
    rankFlight.current = null;
    setRankLoading(false);
    setRankTail(current => ({scope: bufferScope,
      posts: current?.scope === bufferScope ? current.posts : [],
      cursor: head.data[0]?.nextCursor ?? null, pages: 1}));
  }, [head.data, bufferScope]);
  useEffect(() => {
    if (!foreground || insideGroup || !rankTail || rankTail.scope !== bufferScope ||
        !rankTail.cursor || rankTail.pages >= rankDemand || rankFlight.current) return;
    const request = Symbol();
    rankFlight.current = request;
    setRankLoading(true);
    void rankedPage(sort, undefined, rankTail.cursor, 20).then(page => {
      if (rankFlight.current !== request || scopeRef.current !== bufferScope) return;
      setRankTail(current => current?.scope === bufferScope ? {...current,
        posts: [...current.posts, ...page.getposts.items], cursor: page.getposts.nextCursor,
        pages: current.pages + 1} : current);
    }).catch(() => {
      // Existing cards stay usable. A later explicit demand can retry once.
      if (rankFlight.current === request) setRankDemand(rankTail.pages);
    }).finally(() => {
      if (rankFlight.current === request) {rankFlight.current = null; setRankLoading(false);}
    });
  }, [foreground, insideGroup, rankTail, rankDemand, bufferScope, sort]);
  const headIds = useMemo(
    () =>
      (head.data ?? [])
        .filter((item) => !item.post || filterPostVisibility(item.post))
        .map((item) => item.id),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [head.data, user?.itemId, userGroups, isSuperAdmin],
  );

  // Ranking hydrates independently from the numeric direct-read pagination.
  // Its cards can be merged without changing continuation or waiting on Logic.
  const rankedCards = [...(head.data ?? []).flatMap(item => item.post ? [item.post] : []),
    ...(rankTail?.scope === bufferScope ? rankTail.posts : [])]
    .filter(post => !deletedRankIds.current.has(post.ItemId));
  useEffect(() => {
    if (insideGroup || !rankedCards.length || !queryClient.setQueryData) return;
    queryClient.setQueryData(feedQueryKey(undefined, sort, orgId, user?.itemId),
      (old: unknown) => mergeBackgroundCards(old, rankedCards.filter(filterPostVisibility)));
    // Only a new rank response adds cards. Optimistic list updates must not
    // replay the older ranking payload over the current card models.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [head.data, rankTail?.posts, bufferScope]);
  const currentById = new Map(visiblePosts.map(post => [post.ItemId, post]));
  const rankedVisible = rankedCards.map(post => ({
    ...(currentById.get(post.ItemId) ?? post), feedBoostedUntil: post.feedBoostedUntil,
  })).filter(filterPostVisibility);
  const rankedIds = new Set(rankedVisible.map(post => post.ItemId));
  const reconciledPosts = [...rankedVisible, ...visiblePosts.filter(post => !rankedIds.has(post.ItemId))];
  const visibleIdsKey = reconciledPosts.map((p) => p.ItemId).join(",");
  const reconcile = useCallback(
    (acceptAll = false) => {
      if (!stable || !data) return;
      for (const post of reconciledPosts) knownPosts.current.set(post.ItemId, post);
      const result = reconcileFeed(stableFeeds.get(feedKey) ?? null, {
        freshIds: reconciledPosts.map((p) => p.ItemId),
        headIds,
        atTop: atTopRef.current,
        acceptAll: acceptAll || acceptingRef.current || (atTopRef.current && rankedVisible.length > 0),
        isDirect: (id) =>
          !!user?.itemId &&
          knownPosts.current.get(id)?.CreatedBy === user.itemId,
      });
      stableFeeds.set(feedKey, result.state);
      setStableView({
        key: feedKey,
        order: result.state.order,
        pending: result.pending,
      });
    },
    // visiblePosts is derived from data; its id list is the real dependency.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [stable, data, visibleIdsKey, headIds, feedKey, user?.itemId],
  );
  useEffect(() => reconcile(), [reconcile]);
  // The latest reconcile, for code that runs after an await: the closure it
  // started with still holds the data from before that await.
  const reconcileRef = useRef(reconcile);
  reconcileRef.current = reconcile;

  // At the top, a changed head (a boost, a new post, a re-rank) quietly
  // refreshes the list so it re-sorts in place; further down nothing moves and
  // the pill counts what is new.
  const [isShowingNewPosts, setShowingNewPosts] = useState(false);
  /** The pill: load the fresh ranking and show it all. Resolves when shown. */
  //
  // While accepting, every reconcile shows the fresh order in full. That covers
  // the render the refetched data arrives in, which would otherwise see the new
  // posts as unseen and put them straight back behind the pill.
  const showNewPosts = useCallback(async () => {
    setShowingNewPosts(true);
    acceptingRef.current = true;
    atTopRef.current = true;
    try {
      // The reader is taken to the top, so only page 1 is needed now; later
      // pages load again on scroll. Refetching every loaded page first (one
      // ranking call each) kept the pill spinning for that whole sequence.
      queryClient.setQueryData(
        feedQueryKey(undefined, sort, orgId, user?.itemId),
        (old: { pages: unknown[]; pageParams: unknown[] } | undefined) =>
          old
            ? { pages: old.pages.slice(0, 1), pageParams: old.pageParams.slice(0, 1) }
            : old,
      );
      if (!head.data?.length) await refetch();
    } finally {
      setShowingNewPosts(false);
    }
    await new Promise((resolve) => setTimeout(resolve, 0));
    reconcileRef.current(true);
    acceptingRef.current = false;
  }, [refetch, queryClient, sort, orgId, user?.itemId, head.data?.length]);

  /** The feed screen reports its scroll offset here. */
  const setFeedScrollY = useCallback((y: number) => {
    const wasAtTop = atTopRef.current;
    atTopRef.current = y < FEED_TOP_THRESHOLD;
    if (!wasAtTop && atTopRef.current) reconcileRef.current();
  }, []);

  const stablePosts = useMemo(() => {
    if (!stable || !stableView || stableView.key !== feedKey) return null;
    const byId = new Map(reconciledPosts.map((p) => [p.ItemId, p]));
    return stableView.order.flatMap((id) => {
      const post = byId.get(id) ?? knownPosts.current.get(id);
      return post && filterPostVisibility(post) ? [post] : [];
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stable, stableView, feedKey, visibleIdsKey]);

  const newPostsCount =
    stable && stableView?.key === feedKey && !hasSearched
      ? stableView.pending.length
      : 0;

  const feedPosts = stablePosts ?? (insideGroup ? visiblePosts : reconciledPosts);
  const displayedPosts = hasSearched ? searchResults : feedPosts;

  const handleSearch = async (page = 1) => {
    const pageNum = typeof page === "number" ? page : 1;
    if (!search.trim()) {
      searchRequest.current = null;
      isSearchingRef.current = false;
      setIsSearchLoading(false);
      setSearchResults([]);
      setSearchPage(1);
      setHasSearched(false);
      // Only touch the URL when there is actually a param to drop. The reactive
      // effect below runs this on mount, and writing `search: undefined` over an
      // already-empty query is a pointless navigation.
      if (initialSearch) router.setParams({ search: undefined });
      return;
    }
    // Ref, not the state flag: `isSearchLoading` only becomes true after a
    // re-render, and a scroll gesture can call this several times inside one
    // tick — which is how a single flick queued a burst of identical requests.
    if (searchRequest.current?.scope === searchScope) return;
    const request = { scope: searchScope, page: pageNum };
    searchRequest.current = request;
    isSearchingRef.current = true;
    setIsSearchLoading(true);
    setHasSearched(true);
    router.setParams({ search });
    try {
      // Text search runs against the data gateway now. It used to POST the query
      // to the Rails embedding service for a semantic ranking, get back post ids
      // and re-fetch them — two round trips through a service that is out of
      // scope, and a hard dependency for the plainest kind of search. `contains`
      // is a substring match on the gateway (the same operator news search uses),
      // so both branches are now one paged query and share the feed's page size.
      const hashtag = extractHashtag(search);
      const match = hashtag
        ? { hashtags: { in: [hashtag] } }
        : { content: { contains: search.trim() } };
      // Searching inside a group must stay inside that group. Without this the
      // query went out unscoped, so every public post in the tenant that
      // matched came back — and `hasNextPage`/paging counted those discarded
      // rows too.
      const where =
        insideGroup && groupId
          ? { and: [match, groupScopeWhere(groupId)] }
          : match;

      const client = await gqlClient();
      const result = await client.request(GET_POSTS_QUERY, {
        where,
        order: [{ field: "CreatedDate", direction: "DESC" }],
        pageNo: pageNum,
        pageSize: SEARCH_PAGE_SIZE,
      });

      if (searchScopeRef.current !== request.scope || searchRequest.current !== request) return;
      const newPosts = (result.getposts?.items ?? []).filter(
        filterPostVisibility,
      );
      setSearchResults((prev) =>
        pageNum === 1 ? newPosts : [...prev, ...newPosts],
      );
      setHasMoreSearchResults(result.getposts?.hasNextPage ?? false);
      setSearchPage(result.getposts?.pageNo ?? pageNum);
    } catch (e) {
      console.error("Search failed:", e);
    } finally {
      if (searchRequest.current === request) {
        searchRequest.current = null;
        isSearchingRef.current = false;
        setIsSearchLoading(false);
      }
    }
  };

  const handleLoadMoreSearchResults = () => {
    if (hasMoreSearchResults && !isSearchingRef.current) {
      handleSearch(searchPage + 1);
    }
  };

  // Search runs off the debounced query rather than only on submit, which is what
  // makes two things work: typing filters the feed as you go, and arriving with a
  // `?search=` param (tapping a hashtag on a post) searches without the user
  // having to press anything. handleSearch clears the results when the query is
  // empty, so this also handles clearing the box.
  useEffect(() => {
    handleSearch(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedSearch]);

  // A `?search=` param can change while this screen is already mounted — tapping a
  // hashtag on a post in the feed is exactly that. `search` is seeded from the
  // param once via useState, so without this the input and results would ignore
  // the new tag. The equality check is what stops it ping-ponging with the
  // debounce effect above, which writes the same value straight back to the URL.
  useEffect(() => {
    if (initialSearch !== search) setSearch(initialSearch);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialSearch]);

  // Mutations
  const createPost = useCreatePostMutation(groupId ?? undefined);
  const updatePost = useUpdatePostMutation(groupId ?? undefined);
  const deletePost = useDeletePostMutation(groupId ?? undefined);

  const handleSubmit = (
    content: string,
    hashtags: string[],
    mentionedUsers: SelectableItem[],
    visibility: VisibilityType,
    attachmentUrls: string[],
    visibleToGroupIds?: string[],
    visibleToUserIds?: string[],
    groupId?: string,
    lifeEventType?: LifeEventType,
    lifeEventDate?: string,
  ) => {
    if (editPost) {
      updatePost.mutate(
        {
          id: editPost.ItemId,
          content,
          hashtags,
          mentionedUsers,
          visibility,
          attachmentUrls,
          visibleToGroupIds,
          visibleToUserIds,
          // The create branch below has always passed these; this one did not,
          // which is why a life update could be posted but never edited.
          lifeEventType,
          lifeEventDate,
        },
        {
          onSuccess: async (data) => {
            // Notify people added by this edit. Only the *newly* mentioned are
            // notified: re-notifying everyone on every edit would spam anyone
            // already tagged, and creating a post was previously the only path
            // that notified at all — tagging someone by editing told them nothing.
            const authorId = user?.itemId;
            const alreadyMentioned = new Set(
              (editPost.mentionedUsers ?? []).map(
                (u: SelectableItem) => u.itemId,
              ),
            );
            const newlyMentioned = [
              ...new Set(mentionedUsers.map((u: SelectableItem) => u.itemId)),
            ]
              .filter((id) => id !== authorId)
              .filter((id) => !alreadyMentioned.has(id));

            for (const recipientId of newlyMentioned) {
              sendNotification({
                userIds: [recipientId],
                denormalizedPayload: JSON.stringify({
                  type: "mention",
                  message: t("LABEL_NOTIFY_POST_MENTION", {
                    firstName: user?.firstName,
                    lastName: user?.lastName,
                  }),
                  postId: editPost.ItemId,
                  actorId: user?.itemId ?? "",
                  actorName: [user?.firstName, user?.lastName]
                    .filter(Boolean)
                    .join(" "),
                  actorAvatar: user?.profileImageUrl,
                  targetEntityId: editPost.ItemId,
                }),
                configurationName: "mention",
                responseKey: "mention-action",
                responseValue: "post",
              });
            }
          },
        },
      );
      setEditPost(null);
    } else {
      createPost.mutate(
        {
          content,
          hashtags,
          mentionedUsers,
          visibility,
          attachmentUrls,
          score: hot(0, 0, new Date()),
          visibleToGroupIds,
          visibleToUserIds,
          groupId,
          lifeEventType,
          lifeEventDate,
          authorSnapshot: {
            firstName: user?.firstName ?? "",
            lastName: user?.lastName ?? "",
            email: user?.email ?? "",
            profileImageUrl: user?.profileImageUrl ?? "",
            userId: user?.itemId,
          } as any,
        },
        {
          onSuccess: async (data) => {
            // Post mention notifications
            const authorId = user?.itemId;
            const uniqueMentionIds = [
              ...new Set(mentionedUsers.map((u: SelectableItem) => u.itemId)),
            ].filter((id) => id !== authorId);

            for (const recipientId of uniqueMentionIds) {
              sendNotification({
                userIds: [recipientId],
                denormalizedPayload: JSON.stringify({
                  // `type` MUST be "mention" — notification-row.tsx switches on
                  // it to deep-link. "post_mention" fell straight through the
                  // switch, so a delivered mention did nothing when tapped.
                  type: "mention",
                  message: t("LABEL_NOTIFY_POST_MENTION", {
                    firstName: user?.firstName,
                    lastName: user?.lastName,
                  }),
                  postId: data.insertpost.itemId,
                  actorId: user?.itemId ?? "",
                  actorName: [user?.firstName, user?.lastName]
                    .filter(Boolean)
                    .join(" "),
                  actorAvatar: user?.profileImageUrl,
                  targetEntityId: data.insertpost.itemId,
                }),
                configurationName: "mention",
                responseKey: "mention-action",
                responseValue: "post",
              });
            }
          },
        },
      );
    }
    setModalOpen(false);
  };

  const handleDeletePress = (post: Post) => {
    setDeletePostId(post.ItemId);
    setConfirmDeleteOpen(true);
  };

  const handleDeleteConfirm = () => {
    if (!deletePostId) return;
    // Pass the post itself so its uploaded attachments are removed from storage
    // along with the record (see deleteEntityFiles).
    const postToDelete = posts.find((p) => p.ItemId === deletePostId);
    const itemId = deletePostId;
    deletedRankIds.current.add(itemId);
    setRankTail(current => current ? {...current, posts: current.posts.filter(post => post.ItemId !== itemId)} : current);
    deletePost.mutate({ itemId, entity: postToDelete }, {onError: () => {
      deletedRankIds.current.delete(itemId);
      void refetch();
    }});
    const shownState = stableFeeds.get(feedKey);
    if (shownState) {
      stableFeeds.set(feedKey, forgetPost(shownState, deletePostId));
      setStableView((view) =>
        view
          ? {
              ...view,
              order: view.order.filter((id) => id !== deletePostId),
              pending: view.pending.filter((id) => id !== deletePostId),
            }
          : view,
      );
    }
    setConfirmDeleteOpen(false);
    setDeletePostId(null);
  };

  const handleCloseViewer = () => {
    setViewerOpen(false);
    setViewerPostId(null);
    // Clearing the param matters beyond tidiness: it is what lets the *same*
    // post be reopened from a second notification tap, since the effect above
    // reacts to the param changing.
    router.setParams({ post_id: undefined });
  };


  const handleCloseReactorModal = () => {
    setReactorModalVisibility(false);
  }
  /**
   * Ask for the next page — at most once per gesture.
   *
   * Every list calls this instead of `fetchNextPage` directly. The ref is what
   * makes it idempotent: `isFetchingNextPage` only flips on the following
   * render, so a scroll that reaches the end fires the handler several times
   * within one tick and each call saw the same stale `false`. The result was a
   * burst of requests for pages 2, 3, 4… on a single flick.
   *
   * Search results page through their own state, so they route through the
   * same door and inherit the same guard.
   */
  const loadMore = useCallback(() => {
    if (hasSearched) {
      handleLoadMoreSearchResults();
      return;
    }
    if (!insideGroup && rankTail?.cursor && !rankFlight.current) setRankDemand(value => Math.max(value, rankTail.pages + 1));
    if (!hasNextPage || isFetching || isFetchingNextPage || isLoadingMoreRef.current) return;
    isLoadingMoreRef.current = true;
    const scope = bufferScope;
    const entry = buffer.current;
    if (entry?.scope === scope && entry.param === nextParam) {
      setBufferDemand(true);
      void entry.promise.then(page => {
        if (scopeRef.current !== scope || buffer.current !== entry) return;
        const queryKey = feedQueryKey(insideGroup ? groupId : undefined, sort, orgId, user?.itemId);
        queryClient.setQueryData(queryKey, (old: { pages: BufferedPage[]; pageParams: (number | string)[] } | undefined) =>
          old ? { pages: [...old.pages, page], pageParams: [...old.pageParams, entry.param] } : old);
        buffer.current = null;
      }).catch(() => {
        if (scopeRef.current === scope) {
          buffer.current = null;
          // Let the query expose its normal retry/error state on demand.
          return fetchNextPage({ cancelRefetch: false });
        }
      }).finally(() => {
        if (scopeRef.current === scope) { isLoadingMoreRef.current = false; setBufferDemand(false); }
      }).catch(() => {});
      return;
    }
    void fetchNextPage({ cancelRefetch: false }).finally(() => {
      if (scopeRef.current === scope) isLoadingMoreRef.current = false;
    }).catch(() => {});
    // `handleLoadMoreSearchResults` is redefined every render and carries its
    // own guard, so it is deliberately not a dependency.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasSearched, hasNextPage, isFetching, isFetchingNextPage, fetchNextPage, bufferScope, nextParam, insideGroup, groupId, sort, queryClient, stable, rankTail]);

  return {
    displayedPosts,
    newPostsCount,
    showNewPosts,
    isShowingNewPosts,
    setFeedScrollY,
    hasFeedData: !!data,
    isLoading,
    isError,
    refetch,
    fetchNextPage,
    loadMore,
    hasNextPage: hasNextPage || !!rankTail?.cursor,
    prefetchMore,
    isFetchingNextPage: isFetchingNextPage || isBufferDemand || (!hasNextPage && rankLoading),
    search,
    setSearch,
    isSearchLoading,
    handleSearch,
    hasSearched,
    handleLoadMoreSearchResults,
    hasMoreSearchResults,
    modalOpen,
    setModalOpen,
    editPost,
    setEditPost,
    viewerOpen,
    setViewerOpen,
    viewerPostId,
    setViewerPostId,
    handleCloseViewer,
    confirmDeleteOpen,
    setConfirmDeleteOpen,
    deletePostId,
    setDeletePostId,
    handleDeleteConfirm,
    handleDeletePress,
    handleSubmit,
    createPost,
    deletePost,

    sort,
    setSort,
    showReactorModal,
    setReactorModalVisibility,
    handleCloseReactorModal
  };
};
